import { useState, useRef, useEffect } from 'react';
import { Globe } from 'lucide-react';
import { useLanguageStore, Language } from '../store/languageStore';

const languages: { code: Language; name: string; nativeName: string }[] = [
  { code: 'he', name: 'Hebrew', nativeName: 'עברית' },
  { code: 'en', name: 'English', nativeName: 'English' },
  { code: 'ru', name: 'Russian', nativeName: 'Русский' },
  { code: 'si', name: 'Sinhala', nativeName: 'සිංහල' },
  { code: 'uz', name: 'Uzbek', nativeName: 'Oʻzbek' },
  { code: 'ro', name: 'Romanian', nativeName: 'Română' },
];

interface LanguageSwitcherProps {
  position?: 'bottom-left' | 'header';
}

export function LanguageSwitcher({ position = 'header' }: LanguageSwitcherProps) {
  const { language, setLanguage } = useLanguageStore();
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const currentLanguage = languages.find((lang) => lang.code === language) || languages[0];

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    }

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  const handleLanguageSelect = (langCode: Language) => {
    setLanguage(langCode);
    setIsOpen(false);
  };

  if (position === 'bottom-left') {
    return (
      <div className="fixed bottom-4 rtl:left-4 ltr:right-4 z-50" ref={dropdownRef}>
        <button
          onClick={() => setIsOpen(!isOpen)}
          className="flex items-center gap-2 px-3 py-2 bg-white rounded-lg shadow-lg hover:shadow-xl transition-all border border-gray-200"
          aria-label="Change language"
        >
          <Globe size={18} className="text-gray-600 flex-shrink-0" />
          <span className="text-sm font-medium text-gray-700 whitespace-nowrap">{currentLanguage.nativeName}</span>
        </button>

        {isOpen && (
          <div className="absolute bottom-full rtl:left-0 ltr:right-0 mb-2 w-48 bg-white rounded-lg shadow-xl border border-gray-200 overflow-hidden">
            {languages.map((lang) => (
              <button
                key={lang.code}
                onClick={() => handleLanguageSelect(lang.code)}
                className={`w-full rtl:text-right ltr:text-left px-4 py-3 hover:bg-gray-50 transition-colors ${
                  language === lang.code ? 'bg-blue-50 text-blue-700 font-semibold' : 'text-gray-700'
                }`}
              >
                <div className="flex items-center justify-between">
                  <span className="text-sm">{lang.nativeName}</span>
                  {language === lang.code && (
                    <span className="text-blue-600">✓</span>
                  )}
                </div>
              </button>
            ))}
          </div>
        )}
      </div>
    );
  }

  // Header position (default)
  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-2 px-3 py-2 text-gray-600 hover:text-gray-800 transition-colors rounded-lg hover:bg-gray-100"
        aria-label="Change language"
      >
        <Globe size={18} />
        <span className="hidden sm:inline text-sm font-medium">{currentLanguage.nativeName}</span>
      </button>

      {isOpen && (
        <>
          <div
            className="fixed inset-0 z-40"
            onClick={() => setIsOpen(false)}
          />
          <div className="absolute rtl:right-0 ltr:left-0 top-full mt-2 w-48 bg-white rounded-lg shadow-xl border border-gray-200 overflow-hidden z-50">
            {languages.map((lang) => (
              <button
                key={lang.code}
                onClick={() => handleLanguageSelect(lang.code)}
                className={`w-full rtl:text-right ltr:text-left px-4 py-3 hover:bg-gray-50 transition-colors ${
                  language === lang.code ? 'bg-blue-50 text-blue-700 font-semibold' : 'text-gray-700'
                }`}
              >
                <div className="flex items-center justify-between">
                  <span className="text-sm">{lang.nativeName}</span>
                  {language === lang.code && (
                    <span className="text-blue-600">✓</span>
                  )}
                </div>
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

